import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { FaFacebookF, FaInstagram, FaYoutube } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { RefreshCw } from "lucide-react";
import BASE_URL from "../utils";
import { useToast } from "./Toaster";

const menu1 = [
  {
    title: "About",
    path: "/about",
  },
  {
    title: "⁠The Book",
    path: "/the-book",
  },
  {
    title: "The App",
    path: "/the-app",
  },
  {
    title: "The Podcast",
    path: "/the-podcast",
  },
  {
    title: "Recipes & Lifestyle",
    path: "recipes-and-lifestyle",
  },
];

const menu2 = [
  {
    title: "About Author",
    path: "/about",
  },
  {
    title: "Contact",
    path: "/contact",
  },
];

const Footer = () => {
  const toast = useToast();
  const [email, setEmail] = useState("");
  const [captcha, setCaptcha] = useState("");
  const [captchaInput, setCaptchaInput] = useState("");
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [loading, setLoading] = useState(false);
  const captchaInputRef = useRef(null);
  const captchaRef = useRef("");
  const emailRef = useRef("");
  const submittingRef = useRef(false);

  const generateCaptcha = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let code = "";
    for (let i = 0; i < 5; i += 1) {
      code += chars[Math.floor(Math.random() * chars.length)];
    }
    captchaRef.current = code;
    setCaptcha(code);
    setCaptchaInput("");
  };

  useEffect(() => {
    if (showCaptcha) captchaInputRef.current?.focus();
  }, [showCaptcha]);

  const isValidEmail = (value) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

  const handleSubscribeClick = (e) => {
    e?.preventDefault?.();
    if (showCaptcha || loading) return;
    const trimmed = email.trim();
    if (!trimmed) {
      toast.error("Please enter your email.");
      return;
    }
    if (!isValidEmail(trimmed)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    emailRef.current = trimmed;
    generateCaptcha();
    setShowCaptcha(true);
  };

  const submitNewsletter = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setLoading(true);

    try {
      await axios.post(`${BASE_URL || ""}/api/newsletter/subscribe`, {
        email: emailRef.current || email.trim(),
      });
      toast.success("You’re subscribed. Thank you!");
      setEmail("");
      emailRef.current = "";
      setShowCaptcha(false);
      setCaptchaInput("");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
      generateCaptcha();
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  const handleCaptchaChange = (value) => {
    const next = String(value || "")
      .toUpperCase()
      .replace(/\s/g, "");
    setCaptchaInput(next);

    const expected = captchaRef.current;
    if (!expected || loading || submittingRef.current) return;
    if (next.length < expected.length) return;
    if (next === expected) {
      submitNewsletter();
      return;
    }
    toast.error("Captcha does not match.");
  };

  return (
    <footer className="py-10">
      <div className="page-width">
        <div className="footer_inner">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-8">
            <div className="footer_logo_wrapper">
              <Link to="/" aria-label="The Plant Based Switch">
                <img
                  src="/Images/logo.png"
                  alt="The Plant Based Switch"
                  width="180"
                  height="132"
                  className="md:mx-0 logo__"
                  loading="lazy"
                  decoding="async"
                />
              </Link>
              <p>“If You Plant It, It Will Grow!” ™</p>
            </div>

            <div className="footer_menu_block">
              <h3 className="footer_link_title">Quick Links</h3>
              {menu1.map((link, index) => (
                <Link key={index} to={link.path} className="footer_link inline">
                  {link.title}
                </Link>
              ))}
            </div>

            <div className="footer_menu_block">
              <h3 className="footer_link_title">Customer service</h3>
              {menu2.map((link, index) => (
                <Link key={index} to={link.path} className="footer_link inline">
                  {link.title}
                </Link>
              ))}
            </div>

            <div
              id="newsletter"
              className="footer_newsletter_wrapper md:col-span-3 lg:col-span-1"
            >
              <h3 className="footer_link_title">Get Recipes, Tips & Offers</h3>

              <form className="footer_newsletter_form" onSubmit={handleSubscribeClick} noValidate>
                {!showCaptcha ? (
                  <>
                    <label htmlFor="footer-newsletter-email" className="sr-only">
                      Email
                    </label>
                    <input
                      id="footer-newsletter-email"
                      type="email"
                      value={email}
                      placeholder="Email"
                      autoComplete="email"
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    <button type="submit">Subscribe</button>
                  </>
                ) : (
                  <>
                    <p className="footer_newsletter_hint">
                      Enter the code to confirm
                    </p>
                    <div className="footer_newsletter_captcha">
                      <div
                        className="footer_newsletter_captcha_code"
                        aria-hidden="true"
                      >
                        {captcha}
                      </div>
                      <button
                        type="button"
                        className="footer_newsletter_captcha_refresh"
                        onClick={() => {
                          generateCaptcha();
                          captchaInputRef.current?.focus();
                        }}
                        disabled={loading}
                        aria-label="Refresh captcha"
                      >
                        <RefreshCw size={16} />
                      </button>
                    </div>
                    <input
                      ref={captchaInputRef}
                      type="text"
                      inputMode="text"
                      autoCapitalize="characters"
                      autoCorrect="off"
                      spellCheck={false}
                      maxLength={5}
                      value={captchaInput}
                      placeholder="Enter captcha"
                      autoComplete="off"
                      disabled={loading}
                      onChange={(e) => handleCaptchaChange(e.target.value)}
                    />
                  </>
                )}
              </form>

              <h3 className="footer_link_title">Let's Connect</h3>
              <div className="social_media_inner_wrapper flex gap-5">
                <a
                  href="https://www.facebook.com/theplantbasedswitchapp/"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Facebook"
                >
                  <FaFacebookF size={20} />
                </a>
                <a
                  href="https://www.instagram.com/theplantbasedswitch/"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Instagram"
                >
                  <FaInstagram size={20} />
                </a>
                <a
                  href="https://www.youtube.com//watch?v=bF6be24Q9ZY&list=PL3JaWsdDS9V-RXEyer3H2Y1G7wXFFDzqH"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="YouTube"
                >
                  <FaYoutube size={20} />
                </a>
                <a
                  href="https://x.com/theplantbasedswitch"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="X"
                >
                  <FaXTwitter size={20} />
                </a>
              </div>

              <div className="downloadable_links_wrapper flex gap-4 mt-5">
                <a
                  rel="noreferrer"
                  href="https://apps.apple.com/in/app/the-plant-based-switch/id6444078958"
                  target="_blank"
                >
                  <img
                    src="/Images/appstore.png"
                    alt="Download on the App Store"
                    width="240"
                    height="71"
                    loading="lazy"
                    decoding="async"
                  />
                </a>
                <a
                  rel="noreferrer"
                  href="https://play.google.com/store/apps/details?id=com.plant.based.food&pli=1"
                  target="_blank"
                >
                  <img
                    src="/Images/googleplay.png"
                    alt="Get it on Google Play"
                    width="240"
                    height="71"
                    loading="lazy"
                    decoding="async"
                  />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
